import React, { useState, useEffect, useCallback, useRef } from 'react';
import { transactionService } from '../../services/transactionService';

const STATUS_META = {
  F:   { icon: '✓', color: 'var(--success)',    label: 'Zafiskalizowany',                       rowBg: 'transparent' },
  'F!':{ icon: '✗', color: 'var(--danger)',     label: 'Błąd fiskalizacji',                     rowBg: 'rgba(220,53,69,0.12)' },
  NF:  { icon: '?', color: 'var(--warning)',    label: 'Niezafiskalizowany - wymaga fiskalizacji', rowBg: 'rgba(255,193,7,0.12)' },
  ND:  { icon: '–', color: 'var(--text-muted)', label: 'Korekta',                               rowBg: 'transparent' },
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatTime = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && raw.length >= 5) return raw.substring(0, 5);
  return '';
};

const TodayReceiptsModal = ({ isOpen, onClose, locationId, onClickReceipt }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | issues
  // Blokada wielokrotnej fiskalizacji tego samego paragonu (szybkie klikniecia
  // 'Ponow fiskalizacje' fiskalizowaly go kilka razy -> duplikaty na drukarce).
  const fiscalizingRef = useRef(new Set());
  const [fiscalizingIds, setFiscalizingIds] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const today = todayISO();
      const filters = { limit: 500, status: 'completed', date_from: today, date_to: today };
      if (locationId) filters.location_id = locationId;
      const res = await transactionService.getTransactions(filters);
      const list = res?.data?.transactions || res?.transactions || [];
      setItems(list);
    } catch (e) {
      setError('Błąd pobierania paragonów');
    } finally {
      setLoading(false);
    }
  }, [isOpen, locationId]);

  useEffect(() => { load(); }, [load]);

  if (!isOpen) return null;

  const counts = items.reduce((acc, tx) => {
    const s = tx.fiscal_status || 'F!';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const totalAmount = items.reduce((acc, tx) => acc + (parseFloat(tx.total_amount) || 0), 0);

  const visible = filter === 'issues'
    ? items.filter((tx) => ['F!', 'NF'].includes(tx.fiscal_status))
    : items;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          width: '100%',
          maxWidth: '720px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--border, rgba(0,0,0,0.1))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text)' }}>
              Paragony z dzisiaj
            </h3>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {items.length} paragonow / {totalAmount.toFixed(2)} zl
            </div>
          </div>
          <button
            onClick={onClose}
            title="Zamknij"
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '20px',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Filtry + statusy */}
        <div
          style={{
            padding: '0.5rem 1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            borderBottom: '1px solid var(--border, rgba(0,0,0,0.06))',
            fontSize: '11px',
          }}
        >
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border, rgba(0,0,0,0.15))',
                backgroundColor: filter === 'all' ? 'var(--info)' : 'var(--card-bg)',
                color: filter === 'all' ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Wszystkie ({items.length})
            </button>
            <button
              onClick={() => setFilter('issues')}
              disabled={(counts['F!'] || 0) + (counts['NF'] || 0) === 0}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                border: '1px solid var(--border, rgba(0,0,0,0.15))',
                backgroundColor: filter === 'issues' ? 'var(--warning)' : 'var(--card-bg)',
                color: filter === 'issues' ? '#fff' : 'var(--text-secondary)',
                cursor: (counts['F!'] || 0) + (counts['NF'] || 0) === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                opacity: (counts['F!'] || 0) + (counts['NF'] || 0) === 0 ? 0.5 : 1,
              }}
            >
              Problemy ({(counts['F!'] || 0) + (counts['NF'] || 0)})
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--success)' }}>✓ OK: {counts['F'] || 0}</span>
            <span style={{ color: 'var(--danger)' }}>✗ Blad: {counts['F!'] || 0}</span>
            <span style={{ color: 'var(--warning)' }}>? Brak: {counts['NF'] || 0}</span>
            {counts['ND'] > 0 && <span>– Korekta: {counts['ND']}</span>}
          </div>
        </div>

        {/* Lista */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '0.5rem 0' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie…</div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--danger)' }}>{error}</div>
          )}
          {!loading && !error && visible.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Brak paragonow</div>
          )}
          {visible.map((tx) => {
            const status = tx.fiscal_status || 'F!';
            const meta = STATUS_META[status] || STATUS_META['F!'];
            // Przycisk fiskalizacji - tylko dla bledow (F!) i niezafiskalizowanych (NF)
            const needsFiscalize = status === 'F!' || status === 'NF';
            const canFiscalize = !!onClickReceipt && needsFiscalize;
            const total = parseFloat(tx.total_amount) || 0;
            const time = formatTime(tx.transaction_time || tx.czas_transakcji);
            const btnLabel = status === 'F!' ? 'Ponów fiskalizację' :
                             status === 'NF' ? 'Fiskalizuj' : '';
            const btnColor = status === 'F!' ? 'var(--danger)' :
                             status === 'NF' ? 'var(--warning)' : 'var(--text-muted)';
            return (
              <div
                key={tx.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr 60px 90px 100px 130px',
                  gap: '8px',
                  alignItems: 'center',
                  padding: '8px 1.25rem',
                  fontSize: '12px',
                  backgroundColor: meta.rowBg,
                  borderBottom: '1px solid var(--border, rgba(0,0,0,0.04))',
                  color: 'var(--text)',
                }}
              >
                <span style={{ color: meta.color, fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>{meta.icon}</span>
                <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                  {tx.receipt_number || tx.numer_paragonu || `#${tx.id}`}
                </span>
                <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{time}</span>
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {total.toFixed(2)} zl
                </span>
                <span
                  style={{ fontSize: '10px', color: meta.color, fontWeight: 600 }}
                  title={tx.fiskalizacja_blad ? `Blad: ${tx.fiskalizacja_blad}` : meta.label}
                >
                  {meta.label}
                  {tx.fiskalizacja_blad && <span style={{ fontSize: '9px', display: 'block', color: 'var(--text-muted)' }}>ℹ najedz</span>}
                </span>
                {canFiscalize ? (
                  <button
                    disabled={fiscalizingIds.has(tx.id)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (fiscalizingRef.current.has(tx.id)) return; // guard: juz trwa
                      fiscalizingRef.current.add(tx.id);
                      setFiscalizingIds(new Set(fiscalizingRef.current));
                      try {
                        await onClickReceipt(tx);
                      } finally {
                        fiscalizingRef.current.delete(tx.id);
                        setFiscalizingIds(new Set(fiscalizingRef.current));
                        // Odswiez liste po fiskalizacji (status w DB juz zaktualizowany przez auto-verify)
                        await load();
                      }
                    }}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      border: `1px solid ${fiscalizingIds.has(tx.id) ? 'var(--text-muted)' : btnColor}`,
                      backgroundColor: 'var(--card-bg)',
                      color: fiscalizingIds.has(tx.id) ? 'var(--text-muted)' : btnColor,
                      cursor: fiscalizingIds.has(tx.id) ? 'wait' : 'pointer',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                    title="Wyslij paragon do drukarki fiskalnej"
                  >
                    {fiscalizingIds.has(tx.id) ? '⏳ Fiskalizuję...' : `🖨️ ${btnLabel}`}
                  </button>
                ) : (
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
                    {needsFiscalize ? 'Brak drukarki' : '–'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TodayReceiptsModal;
